import { lazy, Suspense, useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import {
  Plus, Search, Star, Sparkles, X, Upload, Image as ImageIcon, Video,
  ExternalLink, Trash2, Pencil, Loader2, Check, LogIn, Quote as QuoteIcon,
  Share2, MoreVertical, Link2, FileSpreadsheet, LayoutGrid, List as ListIcon,
  Eye, CornerDownLeft, ChevronLeft, ChevronRight, Globe, Lock, LogOut, Bell, ShieldCheck,
} from "lucide-react";

const PUBLIC_BOARD_URL = "https://winsboard.felloinnovate.ai";
import { PageTabs } from "@/components/PageTabs";
import { useAuth } from "@/hooks/useAuth";
import { WinShareImageModal } from "@/components/WinShareImage";
import type { Win } from "@/lib/types";

// ── Types ───────────────────────────────────────────────────────────────────
type Platform =
  | "facebook" | "x" | "linkedin" | "instagram" | "email" | "slack"
  | "call" | "sms" | "in_app" | "other";

type MediaType = "image" | "video" | "loom" | "youtube";

interface MediaItem {
  url: string;
  type: MediaType;
  // Set when the video was processed by Mux (HLS + thumbnails + auto captions).
  mux_asset_id?: string;
  mux_playback_id?: string;
}

interface UploadedItem { url: string; kind: string; mux_asset_id?: string; mux_playback_id?: string }
function toMediaItem(it: UploadedItem): MediaItem {
  return {
    url: it.url,
    type: (it.kind === "video" ? "video" : "image") as MediaType,
    ...(it.mux_asset_id && it.mux_playback_id && { mux_asset_id: it.mux_asset_id, mux_playback_id: it.mux_playback_id }),
  };
}

interface WinV2 {
  id: string;
  quote: string;
  title: string | null;
  summary: string | null;
  platform: Platform | null;
  source_url: string | null;
  account: string | null;
  author_name: string | null;
  media_url: string | null;
  media_type: MediaType | null;
  media?: MediaItem[] | null;
  tags: string[];
  starred: boolean;
  visibility?: "external" | "internal" | null;
  reviewed?: boolean;
  created_by_email: string;
  created_by_name: string | null;
  ai_processed: boolean;
  created_at: string;
  updated_at: string;
}

const PLATFORM_LABEL: Record<Platform, string> = {
  facebook: "Facebook",
  x: "X / Twitter",
  linkedin: "LinkedIn",
  instagram: "Instagram",
  email: "Email",
  slack: "Slack",
  call: "Call",
  sms: "SMS",
  in_app: "In-app",
  other: "Other",
};

const PAGE_SIZE = 12;

// ── Media helpers (parity with command center's WinsPanel) ──────────────────
type MediaKind = "image" | "video" | "loom" | "youtube" | "link" | "none";

function normalizeUrl(raw: string): { url: string; kind: MediaKind } {
  if (!raw) return { url: raw, kind: "none" };
  const u = raw.trim();
  const lower = u.toLowerCase();
  const driveMatch = u.match(/drive\.google\.com\/file\/d\/([a-z0-9_-]+)/i);
  if (driveMatch) return { url: `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`, kind: "image" };
  if (lower.includes("dropbox.com")) {
    const raw2 = u.replace(/([?&])dl=0\b/, "$1raw=1");
    if (/\.(jpg|jpeg|png|gif|webp|avif)/.test(lower)) return { url: raw2, kind: "image" };
    if (/\.(mp4|webm|mov)/.test(lower)) return { url: raw2, kind: "video" };
  }
  const imgurMatch = u.match(/^https?:\/\/(?:www\.)?imgur\.com\/([a-z0-9]+)(?:$|\?|#)/i);
  if (imgurMatch) return { url: `https://i.imgur.com/${imgurMatch[1]}.jpg`, kind: "image" };
  if (lower.includes("loom.com")) return { url: u, kind: "loom" };
  if (lower.includes("youtube.com") || lower.includes("youtu.be")) return { url: u, kind: "youtube" };
  if (/\.(jpg|jpeg|png|gif|webp|avif)(\?|$)/.test(lower)) return { url: u, kind: "image" };
  if (/\.(mp4|webm|mov)(\?|$)/.test(lower)) return { url: u, kind: "video" };
  if (/^https?:\/\//i.test(u)) return { url: u, kind: "link" };
  return { url: u, kind: "none" };
}

function embedUrlFor(kind: MediaKind, url: string): string {
  if (kind === "loom") {
    const m = url.match(/loom\.com\/share\/([a-z0-9]+)/i);
    return m ? `https://www.loom.com/embed/${m[1]}` : url;
  }
  if (kind === "youtube") {
    const m = url.match(/(?:v=|\/shorts\/|\.be\/)([A-Za-z0-9_-]{6,})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : url;
  }
  return url;
}

function posterFor(kind: MediaKind, url: string): string | null {
  if (kind === "youtube") {
    const m = url.match(/(?:v=|\/shorts\/|\.be\/)([A-Za-z0-9_-]{6,})/);
    return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
  }
  if (kind === "loom") {
    const m = url.match(/loom\.com\/share\/([a-z0-9]+)/i);
    return m ? `https://cdn.loom.com/sessions/thumbnails/${m[1]}-with-play.gif` : null;
  }
  return null;
}

function hostStyle(urlStr: string): { label: string; gradient: string } {
  let host = "";
  try { host = new URL(urlStr).hostname.replace(/^www\./, ""); } catch { host = "Link"; }
  const h = host.toLowerCase();
  if (h.includes("drive.google") || h.includes("docs.google")) return { label: "Google Drive", gradient: "linear-gradient(135deg,#1A73E8 0%,#34A853 100%)" };
  if (h.includes("dropbox.com")) return { label: "Dropbox", gradient: "linear-gradient(135deg,#0061FF 0%,#27B2FF 100%)" };
  if (h.includes("figma.com")) return { label: "Figma", gradient: "linear-gradient(135deg,#F24E1E 0%,#A259FF 100%)" };
  if (h.includes("notion.so") || h.includes("notion.site")) return { label: "Notion", gradient: "linear-gradient(135deg,#2E2E2E 0%,#757575 100%)" };
  if (h.includes("github.com")) return { label: "GitHub", gradient: "linear-gradient(135deg,#0D1117 0%,#30363D 100%)" };
  if (h.includes("linkedin.com")) return { label: "LinkedIn", gradient: "linear-gradient(135deg,#0A66C2 0%,#4F9CE7 100%)" };
  if (h.includes("slack.com")) return { label: "Slack", gradient: "linear-gradient(135deg,#4A154B 0%,#E01E5A 100%)" };
  if (h.includes("gong.io")) return { label: "Gong", gradient: "linear-gradient(135deg,#7C3BED 0%,#F24E1E 100%)" };
  return { label: host || "Link", gradient: "linear-gradient(135deg,#6366F1 0%,#A855F7 100%)" };
}

function LinkCard({ url, title }: { url: string; title?: string | null }) {
  const { label, gradient } = hostStyle(url);
  let host = "", pathname = "";
  try {
    const parsed = new URL(url);
    host = parsed.hostname.replace(/^www\./, "");
    pathname = parsed.pathname + (parsed.search || "");
  } catch { host = "Link"; }
  const favicon = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`;
  return (
    <a
      href={url} target="_blank" rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="group block w-full overflow-hidden"
    >
      <div className="relative flex min-h-[180px] flex-col justify-between p-5 text-white" style={{ background: gradient }}>
        <div className="pointer-events-none absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.25), transparent 40%), radial-gradient(circle at 80% 90%, rgba(0,0,0,0.2), transparent 40%)",
        }} />
        <div className="relative z-10 flex items-center gap-2">
          <img src={favicon} alt="" className="h-5 w-5 rounded-sm bg-white/90 p-0.5" />
          <span className="text-[11px] font-semibold tracking-wide opacity-95">{label}</span>
        </div>
        <div className="relative z-10">
          {title && <div className="mb-1 font-serif text-lg leading-snug line-clamp-2">{title}</div>}
          <div className="text-[10px] opacity-80 truncate">
            {host}{pathname.length > 1 ? <span className="opacity-70">{pathname}</span> : null}
          </div>
          <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium underline-offset-4 group-hover:underline">
            Open <ExternalLink size={11} />
          </div>
        </div>
      </div>
    </a>
  );
}

function EmbedFacade({ kind, url, eager = false }: { kind: "loom" | "youtube"; url: string; eager?: boolean }) {
  const [active, setActive] = useState(eager);
  const poster = posterFor(kind, url);
  const embedUrl = embedUrlFor(kind, url);
  if (active) {
    return (
      <div className="overflow-hidden bg-black" style={{ aspectRatio: "16 / 9" }}>
        <iframe
          src={embedUrl + (kind === "youtube" ? (embedUrl.includes("?") ? "&autoplay=1" : "?autoplay=1") : "")}
          className="h-full w-full"
          allow="autoplay; fullscreen; picture-in-picture"
          loading="lazy"
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setActive(true); }}
      className="group relative block w-full overflow-hidden bg-black"
      style={{ aspectRatio: "16 / 9" }}
      title={`Play ${kind === "youtube" ? "YouTube" : "Loom"} video`}
    >
      {poster && (
        <img src={poster} alt="" loading="lazy" decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 shadow-lg transition-transform group-hover:scale-105">
          <svg viewBox="0 0 20 20" className="ml-0.5 h-6 w-6 text-black" fill="currentColor" aria-hidden>
            <path d="M6 4.5v11l9-5.5z" />
          </svg>
        </span>
      </div>
      <div className="absolute bottom-2 left-2 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
        {kind === "youtube" ? "YouTube" : "Loom"}
      </div>
    </button>
  );
}

// ── Media upload ────────────────────────────────────────────────────────────
// Images go through the server (small, batched, Supabase). Videos go straight
// from the browser to a one-time Mux direct-upload URL — no size cap and no load
// on our server. If Mux isn't configured (503) videos fall back to the server
// batch route and its 100MB cap.
function putFileToMux(url: string, file: File, onProgress: (loaded: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Video upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error("Video upload failed (network)"));
    xhr.send(file);
  });
}

// Returns null when Mux isn't configured so the caller can fall back.
async function uploadVideoToMux(file: File, onProgress: (loaded: number) => void): Promise<MediaItem | null> {
  const r = await fetch("/api/wins-v2/mux/upload", { method: "POST" });
  if (r.status === 503) return null;
  if (!r.ok) throw new Error(await r.text());
  const { upload_id, url } = await r.json();
  await putFileToMux(url, file, onProgress);
  for (let i = 0; i < 90; i++) {
    const st = await fetch(`/api/wins-v2/mux/upload/${upload_id}`);
    if (!st.ok) throw new Error(await st.text());
    const j: { status: string; asset_id: string | null; playback_id: string | null } = await st.json();
    if (j.asset_id && j.playback_id) {
      return { url: `https://stream.mux.com/${j.playback_id}.m3u8`, type: "video", mux_asset_id: j.asset_id, mux_playback_id: j.playback_id };
    }
    if (["errored", "cancelled", "timed_out"].includes(j.status)) throw new Error(`Mux rejected the video (${j.status})`);
    await new Promise((res) => setTimeout(res, 2000));
  }
  throw new Error("Mux is taking too long to accept the video");
}

// Uploads a mixed set of files, returns MediaItems in the original file order.
async function uploadWinMedia(files: File[], onProgress?: (pct: number) => void): Promise<MediaItem[]> {
  const total = files.reduce((n, f) => n + f.size, 0) || 1;
  const loaded = new Map<number, number>();
  const report = () => onProgress?.(Math.min(100, Math.round(([...loaded.values()].reduce((a, b) => a + b, 0) / total) * 100)));
  const out: (MediaItem | undefined)[] = new Array(files.length);
  const viaServer: number[] = [];
  for (let i = 0; i < files.length; i++) {
    if (!files[i].type.startsWith("video/")) { viaServer.push(i); continue; }
    const item = await uploadVideoToMux(files[i], (l) => { loaded.set(i, l); report(); });
    if (item) { out[i] = item; loaded.set(i, files[i].size); report(); }
    else viaServer.push(i);
  }
  if (viaServer.length) {
    const fd = new FormData();
    for (const i of viaServer) fd.append("files", files[i]);
    const res = await fetch("/api/wins-v2/media/batch", { method: "POST", body: fd });
    if (!res.ok) throw new Error(await res.text());
    const j = await res.json();
    viaServer.forEach((fi, k) => { if (j.items?.[k]) out[fi] = toMediaItem(j.items[k]); });
    for (const i of viaServer) loaded.set(i, files[i].size);
    report();
  }
  return out.filter((m): m is MediaItem => !!m);
}

// Mux Player is a heavy web component — load it only when someone presses play
// (same facade rule as Loom/YouTube: never mount a player per card in a list).
const MuxPlayer = lazy(() => import("@mux/mux-player-react"));

function MuxVideo({
  assetId, playbackId, title, eager,
}: { assetId?: string; playbackId: string; title?: string | null; eager: boolean }) {
  const [active, setActive] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [processing, setProcessing] = useState(false);
  // Once Mux says "ready" we retry the thumbnail once; if it still fails it is a
  // broken thumbnail, NOT a processing video, so never go back to "Processing".
  const [confirmedReady, setConfirmedReady] = useState(false);
  // Follow the video's real shape (portrait phone clips are common) instead of
  // forcing 16:9. Clamped so a 9:16 clip can't make a card absurdly tall.
  const [aspect, setAspect] = useState(16 / 9);
  const frameStyle: React.CSSProperties = {
    aspectRatio: String(aspect),
    ...(eager && { maxHeight: "50vh", width: `min(100%, calc(50vh * ${aspect}))`, margin: "0 auto" }),
  };

  // Mux serves no thumbnail until the asset is ready, so a failed thumbnail is
  // the cheap signal to start polling — ready videos cost zero extra requests.
  useEffect(() => {
    if (!thumbFailed || !assetId || confirmedReady) return;
    setProcessing(true);
    let stopped = false;
    let tries = 0;
    const tick = async () => {
      tries++;
      try {
        const r = await fetch(`/api/wins-v2/mux/${assetId}`);
        const j = r.ok ? await r.json() : null;
        if (j?.status === "ready") { if (!stopped) { setConfirmedReady(true); setThumbFailed(false); setProcessing(false); } return; }
        if (j?.status === "errored") { if (!stopped) setProcessing(false); return; }
      } catch { /* retry */ }
      if (!stopped && tries < 100) timer = window.setTimeout(tick, 4000);
      else if (!stopped) setProcessing(false);
    };
    let timer = window.setTimeout(tick, 1500);
    return () => { stopped = true; window.clearTimeout(timer); };
  }, [thumbFailed, assetId, confirmedReady]);

  if (active) {
    return (
      <div className="overflow-hidden bg-black" style={frameStyle} onClick={(e) => e.stopPropagation()}>
        <Suspense fallback={<div className="h-full w-full grid place-items-center text-white/70"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
          <MuxPlayer
            playbackId={playbackId}
            metadata={{ video_title: title ?? "Felix win" }}
            accentColor="#ff6b5a"
            autoPlay
            style={{ width: "100%", height: "100%" }}
          />
        </Suspense>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (!processing) setActive(true); }}
      disabled={processing}
      className="group relative block w-full overflow-hidden bg-black"
      style={frameStyle}
      title={processing ? "Processing video…" : "Play video"}
    >
      {!thumbFailed && (
        <img
          src={`https://image.mux.com/${playbackId}/thumbnail.jpg?width=${eager ? 960 : 640}`}
          alt="" loading={eager ? "eager" : "lazy"} decoding="async"
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            if (w && h) setAspect(Math.min(16 / 9, Math.max(4 / 5, w / h)));
          }}
          onError={() => setThumbFailed(true)}
          className="absolute inset-0 h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10" />
      <div className="absolute inset-0 flex items-center justify-center">
        {processing ? (
          <span className="inline-flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing video…
          </span>
        ) : (
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 shadow-lg transition-transform group-hover:scale-105">
            <svg viewBox="0 0 20 20" className="ml-0.5 h-6 w-6 text-black" fill="currentColor" aria-hidden>
              <path d="M6 4.5v11l9-5.5z" />
            </svg>
          </span>
        )}
      </div>
    </button>
  );
}

// A win's media is an ordered list. New wins store it in `media`; legacy wins
// only have the single media_url/media_type — normalize both to one shape so
// the rest of the UI never has to care which a win came from. Index 0 is the
// hero (also mirrored to media_url by the server for back-compat).
function getMediaItems(win: WinV2): MediaItem[] {
  if (Array.isArray(win.media) && win.media.length) {
    return win.media.filter((m) => m && typeof m.url === "string" && m.url);
  }
  if (win.media_url) return [{ url: win.media_url, type: (win.media_type ?? "image") as MediaType }];
  return [];
}

// Renders a single media item. `eager` doubles as the "reader/modal" signal: in
// the card grid we want a tasteful cropped preview (object-cover, 540px ceiling);
// in the reader we want the WHOLE screenshot visible without forcing the modal
// past the viewport — contain + viewport-based ceiling.
function MediaView({
  url, type, title, eager = false, muxAssetId, muxPlaybackId,
}: {
  url: string; type?: MediaType | null; title?: string | null; eager?: boolean;
  muxAssetId?: string; muxPlaybackId?: string;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  if (!url) return null;
  const normalized = normalizeUrl(url);
  const kind: MediaKind = (type ?? normalized.kind) as MediaKind;
  const src = normalized.url;
  if (kind === "image" && !imageFailed) {
    return (
      <img src={src} alt={title ?? ""}
        loading={eager ? "eager" : "lazy"} decoding="async"
        onError={() => setImageFailed(true)}
        className={eager
          ? "max-w-full max-h-[50vh] w-auto object-contain mx-auto"
          : "w-full object-cover max-h-[540px]"}
      />
    );
  }
  if (kind === "image" && imageFailed) return <LinkCard url={url} title={title} />;
  if (kind === "video" && muxPlaybackId) {
    return <MuxVideo assetId={muxAssetId} playbackId={muxPlaybackId} title={title} eager={eager} />;
  }
  if (kind === "video") {
    return <video src={src} controls preload={eager ? "metadata" : "none"}
      className={eager ? "max-w-full max-h-[50vh] w-auto bg-black mx-auto" : "w-full bg-black"} />;
  }
  if (kind === "loom" || kind === "youtube") return <EmbedFacade kind={kind} url={src} eager={eager} />;
  if (kind === "link") return <LinkCard url={url} title={title} />;
  return null;
}

// Paged gallery for a win's media. Single item → renders bare MediaView, no
// chrome. Multiple → prev/next arrows, dot indicators, and ←/→ keyboard nav.
function WinGallery({
  items, title, eager = false,
}: { items: MediaItem[]; title?: string | null; eager?: boolean }) {
  const [idx, setIdx] = useState(0);
  const count = items.length;
  const safeIdx = Math.min(idx, Math.max(0, count - 1));
  const go = useCallback((delta: number) => {
    setIdx((i) => {
      const n = items.length;
      if (n <= 0) return 0;
      return (((Math.min(i, n - 1) + delta) % n) + n) % n;
    });
  }, [items.length]);

  useEffect(() => {
    if (count <= 1) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, go]);

  if (!count) return null;
  const current = items[safeIdx];
  if (count === 1) return <MediaView url={current.url} type={current.type} title={title} eager={eager} muxAssetId={current.mux_asset_id} muxPlaybackId={current.mux_playback_id} />;

  return (
    <div className="relative w-full">
      <MediaView key={current.url} url={current.url} type={current.type} title={title} eager={eager} muxAssetId={current.mux_asset_id} muxPlaybackId={current.mux_playback_id} />
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); go(-1); }}
        aria-label="Previous"
        className="absolute left-2 top-1/2 -translate-y-1/2 grid place-items-center w-9 h-9 rounded-full bg-black/55 text-white hover:bg-black/75 transition"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); go(1); }}
        aria-label="Next"
        className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center w-9 h-9 rounded-full bg-black/55 text-white hover:bg-black/75 transition"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      <div className="absolute top-2 right-2 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white">
        {safeIdx + 1}/{count}
      </div>
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={(e) => { e.stopPropagation(); setIdx(i); }}
            aria-label={`Go to image ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === safeIdx ? "w-5 bg-white" : "w-1.5 bg-white/55 hover:bg-white/80"}`}
          />
        ))}
      </div>
    </div>
  );
}

// ── Share text builder (parity with command center) ─────────────────────────
// Share URLs always point to the public board (winsboard.*) so anyone with
// the link can view, with the win pre-opened in the reader. The board is
// no-auth, so external recipients work too.
function shareUrlForWin(win: WinV2): string {
  return `${PUBLIC_BOARD_URL}/?win=${encodeURIComponent(win.id)}`;
}

function buildShareText(win: WinV2): string {
  const lines: string[] = [];
  const head = `Felix Win${win.title ? ` — ${win.title}` : ""}`;
  lines.push(head);
  if (win.quote) lines.push(`"${win.quote}"`);
  const attribution = [win.account, win.author_name, win.platform && PLATFORM_LABEL[win.platform]]
    .filter(Boolean).join(" · ");
  if (attribution) lines.push(`— ${attribution}`);
  if (win.media_url) lines.push(win.media_url);
  if (win.source_url) lines.push(win.source_url);
  if (win.tags?.length) lines.push(win.tags.map((t) => `#${t}`).join(" "));
  lines.push(shareUrlForWin(win));
  lines.push("— Felix Wins");
  return lines.join("\n");
}

async function copyWinText(win: WinV2): Promise<void> {
  await navigator.clipboard.writeText(buildShareText(win));
}

// ── WinV2 → Win adapter for the share-image modal ───────────────────────────
function toShareImageWin(w: WinV2): Win {
  const typeForWin: Win["type"] =
    w.platform === "call" ? "call" :
    w.platform === "sms" ? "text" :
    w.platform === "email" ? "email" :
    "quote";
  return {
    id: 0,
    date: new Date(w.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    type: typeForWin,
    account: w.account ?? "",
    quote: w.quote,
    source: [w.author_name, w.platform && PLATFORM_LABEL[w.platform]].filter(Boolean).join(" · ") || "",
    tags: w.tags ?? [],
    starred: w.starred,
    mediaType: (w.media_type ?? "none") as Win["mediaType"],
    mediaUrl: w.media_url ?? undefined,
    title: w.title ?? undefined,
    author: w.author_name ?? undefined,
  };
}

// ── Confetti burst (CSS-only) ───────────────────────────────────────────────
function ConfettiBurst({ trigger }: { trigger: number }) {
  if (trigger === 0) return null;
  const bits = Array.from({ length: 40 });
  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[100] overflow-hidden" key={trigger}>
      {bits.map((_, i) => {
        const left = Math.random() * 100;
        const dur = 1 + Math.random();
        const delay = Math.random() * 0.3;
        const colors = ["#f5735a", "#14bacb", "#8d6ae6", "#f59e0b", "#22c55e", "#ec67a0"];
        const color = colors[i % colors.length];
        const rot = Math.random() * 360;
        return (
          <span key={i} aria-hidden style={{
            position: "absolute", left: `${left}%`, top: "-10px",
            width: 8, height: 14, background: color,
            transform: `rotate(${rot}deg)`,
            animation: `winsv2-confetti ${dur}s ease-in ${delay}s forwards`,
            borderRadius: 2,
          }} />
        );
      })}
      <style>{`
        @keyframes winsv2-confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.85; }
        }
      `}</style>
    </div>,
    document.body
  );
}

// ── Root ────────────────────────────────────────────────────────────────────
export default function WinsApp({ boardMode = false, embedMode = false }: { boardMode?: boolean; embedMode?: boolean } = {}) {
  const { user, isLoading, isAuthenticated } = useAuth();
  // Board mode: no auth gate — anyone with the link sees the read-only wall.
  if (boardMode) {
    return <WinsAppShell userName="" boardMode />;
  }
  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))]">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--muted-foreground))]" />
      </div>
    );
  }
  if (!isAuthenticated) {
    // Embed mode: open the login in the parent window so the OAuth redirect
    // doesn't get trapped inside the iframe.
    if (embedMode) {
      return (
        <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] p-6">
          <div className="text-center">
            <p className="text-[hsl(var(--muted-foreground))] mb-4">Sign in to view Felix Wins</p>
            <a
              href="/auth/google"
              target="_top"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-white font-medium hover:opacity-90 transition"
            >
              <LogIn className="w-4 h-4" /> Sign in with Google
            </a>
          </div>
        </div>
      );
    }
    // Sharing case: someone copied a felixwins URL with ?win=<id> and a
    // recipient without SSO is opening it. Forward them to the public board
    // so they see the shared win instead of a login screen they can't pass.
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const winId = params.get("win");
      if (winId) {
        window.location.replace(`${PUBLIC_BOARD_URL}/?win=${encodeURIComponent(winId)}`);
        return (
          <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] text-[hsl(var(--muted-foreground))]">
            Redirecting to the public board…
          </div>
        );
      }
    }
    return <LoginScreen />;
  }
  return <WinsAppShell userName={user?.name ?? ""} embedded={embedMode} />;
}

function LoginScreen() {
  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-[#fff7f3] via-[#fefbf9] to-[#f3f6ff] p-6">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl border border-black/5 p-10 text-center">
        <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-[hsl(var(--primary))] text-white grid place-items-center">
          <Sparkles className="w-7 h-7" />
        </div>
        <h1 className="font-serif text-3xl mb-2">Felix Wins</h1>
        <p className="text-[hsl(var(--muted-foreground))] mb-8">
          Sign in with your <span className="font-medium text-[hsl(var(--foreground))]">@fello.ai</span> account to capture and share praise from clients.
        </p>
        <a href="/auth/google" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-white font-medium hover:opacity-90 transition">
          <LogIn className="w-4 h-4" /> Sign in with Google
        </a>
      </div>
    </div>
  );
}

// ── Filter pills ────────────────────────────────────────────────────────────
type Filter = "all" | "starred" | "quote" | "image" | "video" | "external" | "internal";
const FILTERS: { id: Filter; label: string; icon: typeof Star | null }[] = [
  { id: "all", label: "All", icon: null },
  { id: "external", label: "External", icon: Globe },
  { id: "internal", label: "Internal", icon: Lock },
  { id: "starred", label: "Starred", icon: Star },
  { id: "quote", label: "Quotes", icon: QuoteIcon },
  { id: "image", label: "Images", icon: ImageIcon },
  { id: "video", label: "Video", icon: Video },
];
// Visibility-scoped filters only make sense on the internal felixwins surface —
// the public board already shows external-only.
const VISIBILITY_FILTER_IDS = new Set<Filter>(["external", "internal"]);

function winVisibility(w: WinV2): "external" | "internal" {
  return w.visibility === "external" ? "external" : "internal";
}

function matchesFilter(w: WinV2, f: Filter): boolean {
  if (f === "all") return true;
  if (f === "external" || f === "internal") return winVisibility(w) === f;
  if (f === "starred") return w.starred;
  if (f === "quote") return !w.media_url;
  if (f === "image") {
    if (!w.media_url) return false;
    const k = (w.media_type ?? normalizeUrl(w.media_url).kind) as string;
    return k === "image";
  }
  if (f === "video") {
    if (!w.media_url) return false;
    const k = (w.media_type ?? normalizeUrl(w.media_url).kind) as string;
    return k === "video" || k === "loom" || k === "youtube";
  }
  return true;
}

// ── Header overflow menu ────────────────────────────────────────────────────
function HeaderMenu({ onImport, isAdmin }: { onImport: () => void; isAdmin: boolean }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  return (
    <div ref={triggerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-[hsl(var(--border))] bg-white text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{ top: (triggerRef.current?.getBoundingClientRect().bottom ?? 0) + 6, right: window.innerWidth - (triggerRef.current?.getBoundingClientRect().right ?? 0) }}
          className="fixed z-[9999] w-48 rounded-xl bg-white border border-[hsl(var(--border))] shadow-lg py-1.5 text-[13px]"
        >
          <button
            type="button"
            onClick={() => { setOpen(false); onImport(); }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition"
          >
            <FileSpreadsheet className="w-4 h-4 text-[hsl(var(--muted-foreground))]" /> Import CSV
          </button>
          {isAdmin && (
            <a
              href="?admin=1"
              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition"
            >
              <ShieldCheck className="w-4 h-4 text-[hsl(var(--muted-foreground))]" /> Admin panel
            </a>
          )}
          <div className="my-1.5 border-t border-[hsl(var(--border))]" />
          <a
            href="/auth/logout"
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-left text-red-600 hover:bg-red-50 transition"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </a>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ── Shell ───────────────────────────────────────────────────────────────────
function looksLikeQuestion(s: string): boolean {
  const t = s.trim().toLowerCase();
  if (t.length < 6) return false;
  if (t.endsWith("?")) return true;
  return /^(what|who|how|why|which|when|where|does|do|is|are|can|should|would|could|will|tell me|show me|find|any)\b/.test(t);
}

interface AskResult {
  answer: string;
  cited_ids: string[];
  corpus_size: number;
  question: string;
}

export function WinsAppShell({
  userName: _userName,
  embedded = false,
  boardMode = false,
}: {
  userName: string;
  embedded?: boolean;
  boardMode?: boolean;
}) {
  const { isAdmin, canTagVisibility } = useAuth();
  const canReview = isAdmin || canTagVisibility;
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(1);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingWin, setEditingWin] = useState<WinV2 | null>(null);
  const [readerWin, setReaderWin] = useState<WinV2 | null>(null);
  const [shareImageWin, setShareImageWin] = useState<WinV2 | null>(null);
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const [askLoading, setAskLoading] = useState(false);
  const [askResult, setAskResult] = useState<AskResult | null>(null);
  const [askError, setAskError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [view, setView] = useState<"grid" | "list">(() => {
    try { return (localStorage.getItem("wins-v2:view") as "grid" | "list") || "grid"; } catch { return "grid"; }
  });
  useEffect(() => {
    try { localStorage.setItem("wins-v2:view", view); } catch { /* ignore */ }
  }, [view]);

  const winsEndpoint = boardMode ? "/api/wins-v2/public" : "/api/wins-v2";
  const askEndpoint = boardMode ? "/api/wins-v2/public/ask" : "/api/wins-v2/ask";
  const { data: wins = [], isLoading, error: winsError } = useQuery({
    queryKey: ["wins-v2", boardMode ? "public" : "private"],
    queryFn: async (): Promise<WinV2[]> => {
      const res = await fetch(winsEndpoint);
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 200) || res.statusText}`);
      }
      return res.json();
    },
    retry: 1,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return wins.filter((w) => {
      if (!matchesFilter(w, filter)) return false;
      if (!q) return true;
      return [w.quote, w.title, w.summary, w.account, w.author_name, ...(w.tags ?? [])]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q));
    });
  }, [wins, search, filter]);

  const needsReviewCount = useMemo(() => wins.filter((w) => winVisibility(w) === "internal" && !w.reviewed).length, [wins]);

  // Reset to page 1 whenever filter/search changes.
  useEffect(() => { setPage(1); }, [filter, search]);

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = filtered.length > visible.length;

  const handleSaved = useCallback((opts: { wasNew: boolean }) => {
    setComposerOpen(false);
    setEditingWin(null);
    qc.invalidateQueries({ queryKey: ["wins-v2"] });
    if (opts.wasNew) setConfettiTrigger((n) => n + 1);
  }, [qc]);

  const isQuestion = looksLikeQuestion(search);

  async function askFelix() {
    const q = search.trim();
    if (!q || askLoading) return;
    setAskError(null);
    setAskLoading(true);
    try {
      const res = await fetch(askEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q }),
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      setAskResult({ ...j, question: q });
    } catch (e: unknown) {
      setAskError(e instanceof Error ? e.message.slice(0, 300) : String(e));
    } finally {
      setAskLoading(false);
    }
  }
  function clearAsk() {
    setAskResult(null);
    setAskError(null);
  }

  const winsById = useMemo(() => {
    const m = new Map<string, WinV2>();
    for (const w of wins) m.set(String(w.id), w);
    return m;
  }, [wins]);

  // ?win=<id> deep-link — open that win in the reader as soon as the corpus
  // loads. Runs once per win id we land on so toggling around doesn't keep
  // re-opening the same modal. We also flip `initialUrlProcessedRef` after
  // the first wins-load so the URL-sync effect below knows it's safe to
  // start mutating history (before that, it would race the deep-link and
  // wipe the inbound ?win= param).
  const openedWinRef = useRef<string | null>(null);
  const initialUrlProcessedRef = useRef(false);
  useEffect(() => {
    if (!wins.length) return;
    initialUrlProcessedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const winId = params.get("win");
    if (!winId || openedWinRef.current === winId) return;
    const w = winsById.get(winId);
    if (w) {
      openedWinRef.current = winId;
      setReaderWin(w);
    }
  }, [wins, winsById]);

  // Keep the URL in sync with the open reader so the address bar IS the
  // share link — open a win → `?win=<id>` is pushed; close it → param removed.
  // pushState (not replaceState) so back/forward navigates between wins.
  // Gated on the deep-link having had its chance to run; otherwise on a fresh
  // load with ?win=<id>, this effect would fire first (readerWin=null) and
  // strip the param before the deep-link could open the requested win.
  useEffect(() => {
    if (!initialUrlProcessedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const currentParam = params.get("win");
    const target = readerWin ? String(readerWin.id) : null;
    if (currentParam === target) return;
    if (target) params.set("win", target); else params.delete("win");
    const qs = params.toString();
    const url = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
    window.history.pushState({}, "", url);
    openedWinRef.current = target;
  }, [readerWin]);

  // Browser back/forward → reflect URL into reader state.
  useEffect(() => {
    function onPop() {
      const params = new URLSearchParams(window.location.search);
      const winId = params.get("win");
      if (!winId) { setReaderWin(null); openedWinRef.current = null; return; }
      const w = winsById.get(winId);
      if (w) { setReaderWin(w); openedWinRef.current = winId; }
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [winsById]);

  // View-mode toggle wiring. On felixwins.* (where the user has SSO), the
  // View-only button flips the page into boardMode in-place via ?view=1; the
  // mirror "Exit view mode" button comes back when boardMode is on. On the
  // command-center embedded surface, we keep the external link to winsboard
  // since toggling inside the command center would re-render the whole shell.
  const hostName = typeof window !== "undefined" ? window.location.hostname : "";
  const onFelixwinsDomain = hostName.startsWith("felixwins.") ||
    new URLSearchParams(window.location.search).get("wins") === "1";
  const viewToggleHref = onFelixwinsDomain
    ? (() => {
        const params = new URLSearchParams(window.location.search);
        if (boardMode) params.delete("view"); else params.set("view", "1");
        const qs = params.toString();
        return `${window.location.pathname}${qs ? `?${qs}` : ""}`;
      })()
    : PUBLIC_BOARD_URL;
  const viewToggleLabel = boardMode ? "Exit view mode" : "View-only board";
  const viewToggleTarget = onFelixwinsDomain ? undefined : "_blank";

  return (
    <div className={embedded ? "min-h-full" : "min-h-screen bg-[#fefbf9]"}>
      {/* Continuous gentle coral glow on the Ask Felix button so it stays
          discoverable without needing a question-shaped query first. */}
      <style>{`
        @keyframes wins-ask-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(141, 106, 230, 0); }
          50%       { box-shadow: 0 0 0 5px rgba(141, 106, 230, 0.28), 0 0 18px 2px rgba(236, 103, 160, 0.22); }
        }
        .wins-ask-glow { animation: wins-ask-glow 2.2s ease-in-out infinite; }
        @keyframes wins-ask-spark {
          0%, 100% { opacity: 0.85; transform: scale(1); }
          50%       { opacity: 1;    transform: scale(1.12); }
        }
        .wins-ask-spark { animation: wins-ask-spark 2.2s ease-in-out infinite; transform-origin: center; }
        /* Search input picks up a softer, lower-amplitude version of the same
           glow when the typed text looks like a question — signals that
           pressing Enter will fire the AI ask instead of the keyword filter. */
        @keyframes wins-search-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(141, 106, 230, 0); }
          50%       { box-shadow: 0 0 0 3px rgba(141, 106, 230, 0.18); }
        }
        .wins-search-glow { animation: wins-search-glow 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .wins-ask-glow, .wins-ask-spark, .wins-search-glow { animation: none; }
        }
      `}</style>
      {/* Unified header — two rows, single 1px bottom border anchors it.
          Row 1 (60px): brand · search · actions. Search is absolute-centered
          so it sits at the true optical center of the page width regardless
          of the asymmetric brand vs. actions widths.
          Row 2 (44px): filters · view toggle, separated from row 1 by a
          subtle top divider so they read as connected sub-nav, not orphan. */}
      <header className={`${embedded ? "" : "sticky top-0"} z-30 bg-white border-b border-[hsl(var(--border))]`}>
        {/* Brand + global search + primary actions — only when standalone.
            Inside the command center, the parent shell already owns brand
            and global nav, so this row would be a redundant double-header. */}
        {!embedded && (
          <div className="max-w-7xl mx-auto px-6 h-[60px] flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))] text-white grid place-items-center shrink-0">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="leading-[1.15] min-w-0">
                  <div className="text-[16px] font-medium text-[hsl(var(--foreground))] truncate">Felix Wins</div>
                  <div className="text-[11px] font-normal text-[hsl(var(--muted-foreground))]/80 truncate mt-0.5">A wall of praise from our clients</div>
                </div>
              </div>
              {/* Page tabs sit with the brand; search flows between this group
                  and the actions so a third tab can't run under it. */}
              {!boardMode && (
                <PageTabs active="wins" className="ml-2" />
              )}
            </div>
            <div className="flex-1 min-w-0 max-w-[400px]">
              <SearchInput
                value={search}
                onChange={setSearch}
                isQuestion={isQuestion}
                askLoading={askLoading}
                onAsk={askFelix}
              />
            </div>
            <div className="flex items-center gap-2">
              {!boardMode && (
                <>
                  <a
                    href={viewToggleHref}
                    target={viewToggleTarget}
                    rel={viewToggleTarget ? "noopener noreferrer" : undefined}
                    title={boardMode ? "Switch back to editor mode" : (onFelixwinsDomain ? "Flip this page into view-only mode" : "Open the public read-only wall in a new tab")}
                    className="h-9 inline-flex items-center gap-1.5 px-3 rounded-lg border border-[hsl(var(--border))] bg-white text-[13px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition"
                  >
                    <Eye className="w-4 h-4" /> {viewToggleLabel}
                  </a>
                  <button
                    type="button"
                    onClick={() => { setEditingWin(null); setComposerOpen(true); }}
                    className="h-9 inline-flex items-center gap-1.5 px-3 rounded-lg bg-[hsl(var(--primary))] text-white text-[13px] font-medium hover:opacity-90 transition"
                  >
                    <Plus className="w-4 h-4" /> Add a win
                  </button>
                  <HeaderMenu
                    onImport={() => setImportOpen(true)}
                    isAdmin={isAdmin}
                  />
                </>
              )}
              {boardMode && (
                <a
                  href={viewToggleHref}
                  target={viewToggleTarget}
                  rel={viewToggleTarget ? "noopener noreferrer" : undefined}
                  title="Switch back to editor mode"
                  className="h-9 inline-flex items-center gap-1.5 px-3 rounded-lg border border-[hsl(var(--border))] bg-white text-[13px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition"
                >
                  <Eye className="w-4 h-4" /> {viewToggleLabel}
                </a>
              )}
            </div>
          </div>
        )}
        {/* Embedded mode — compact toolbar row with search + actions inline
            with the filters, no brand block. */}
        {embedded && (
          <div className="max-w-7xl mx-auto px-6 pt-4 pb-3 flex items-center gap-3 flex-wrap">
            {!boardMode && (
              <PageTabs active="wins" />
            )}
            <div className="flex-1 min-w-[240px] max-w-[420px]">
              <SearchInput
                value={search}
                onChange={setSearch}
                isQuestion={isQuestion}
                askLoading={askLoading}
                onAsk={askFelix}
              />
            </div>
            <div className="flex items-center gap-2">
              <a
                href={viewToggleHref}
                target={viewToggleTarget}
                rel={viewToggleTarget ? "noopener noreferrer" : undefined}
                title={boardMode ? "Switch back to editor mode" : (onFelixwinsDomain ? "Flip this page into view-only mode" : "Open the public read-only wall in a new tab")}
                className="h-9 inline-flex items-center gap-1.5 px-3 rounded-lg border border-[hsl(var(--border))] bg-white text-[13px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition"
              >
                <Eye className="w-4 h-4" /> {viewToggleLabel}
              </a>
              {!boardMode && (
                <>
                  <button
                    type="button"
                    onClick={() => { setEditingWin(null); setComposerOpen(true); }}
                    className="h-9 inline-flex items-center gap-1.5 px-3 rounded-lg bg-[hsl(var(--primary))] text-white text-[13px] font-medium hover:opacity-90 transition"
                  >
                    <Plus className="w-4 h-4" /> Add a win
                  </button>
                  <HeaderMenu
                    onImport={() => setImportOpen(true)}
                    isAdmin={isAdmin}
                  />
                </>
              )}
            </div>
          </div>
        )}
        {/* Filters + view toggle */}
        <div className={`max-w-7xl mx-auto px-6 h-11 flex items-center gap-1.5 overflow-x-auto ${embedded ? "" : "border-t border-[hsl(var(--border))]/60"}`}>
          {FILTERS.filter(({ id }) => !boardMode || !VISIBILITY_FILTER_IDS.has(id)).map(({ id, label, icon: Icon }) => {
            const active = filter === id;
            return (
              <button
                key={id}
                onClick={() => setFilter(id)}
                className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] border transition whitespace-nowrap ${
                  active
                    ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                    : "bg-white border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                }`}
              >
                {Icon && <Icon className={`w-3.5 h-3.5 ${id === "starred" && active ? "fill-white" : ""}`} />}
                {label}
              </button>
            );
          })}
          {!boardMode && canReview && needsReviewCount > 0 && (
            <button
              type="button"
              onClick={() => setFilter("internal")}
              className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] font-medium border transition whitespace-nowrap ${
                filter === "internal"
                  ? "bg-amber-500 text-white border-amber-500"
                  : "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
              }`}
            >
              <Bell className="w-3.5 h-3.5" />
              {needsReviewCount} to review
            </button>
          )}
          <div className="flex-1" />
          {!boardMode && (
            <div className="inline-flex items-center rounded-lg border border-[hsl(var(--border))] bg-white p-0.5 text-[hsl(var(--muted-foreground))]">
              <button
                type="button"
                onClick={() => setView("grid")}
                title="Grid view"
                className={`h-6 px-2 rounded-md inline-flex items-center gap-1 text-[12px] ${view === "grid" ? "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]" : "hover:text-[hsl(var(--foreground))]"}`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Grid
              </button>
              <button
                type="button"
                onClick={() => setView("list")}
                title="List view"
                className={`h-6 px-2 rounded-md inline-flex items-center gap-1 text-[12px] ${view === "list" ? "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]" : "hover:text-[hsl(var(--foreground))]"}`}
              >
                <ListIcon className="w-3.5 h-3.5" /> List
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {(askResult || askError) && (
          <AnswerCard
            result={askResult}
            error={askError}
            winsById={winsById}
            onClose={clearAsk}
            onOpenWin={(w) => setReaderWin(w)}
          />
        )}
        {winsError && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-900">
            <div className="font-medium mb-0.5">Couldn't load wins.</div>
            <div className="text-red-800/80">{(winsError as Error).message}</div>
            <div className="mt-1 text-red-800/70">
              Try a hard refresh (Cmd/Ctrl+Shift+R). If that doesn't help, open DevTools → Console and run{" "}
              <code className="font-mono bg-red-100 px-1 rounded">localStorage.clear(); location.reload()</code>.
            </div>
          </div>
        )}
        {isLoading ? (
          <div className="py-24 grid place-items-center text-[hsl(var(--muted-foreground))]">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : visible.length === 0 ? (
          <EmptyState onAdd={() => setComposerOpen(true)} hasFilter={!!search || filter !== "all"} boardMode={boardMode} />
        ) : (
          <>
            {view === "grid" ? (
              <div className="columns-1 md:columns-2 xl:columns-3 2xl:columns-4 gap-5 [column-fill:_balance]">
                {visible.map((w) => (
                  <WinCard
                    key={w.id}
                    win={w}
                    boardMode={boardMode}
                    onOpen={() => setReaderWin(w)}
                    onEdit={() => { setEditingWin(w); setComposerOpen(true); }}
                    onShareImage={() => setShareImageWin(w)}
                  />
                ))}
              </div>
            ) : (
              <WinsListView
                wins={visible}
                boardMode={boardMode}
                onOpen={(w) => setReaderWin(w)}
                onEdit={(w) => { setEditingWin(w); setComposerOpen(true); }}
                onShareImage={(w) => setShareImageWin(w)}
              />
            )}
            {hasMore && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setPage((p) => p + 1)}
                  className="px-5 py-2 rounded-xl border border-[hsl(var(--border))] bg-white hover:bg-[hsl(var(--muted))] text-sm"
                >
                  Load more · {filtered.length - visible.length} left
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {!boardMode && composerOpen && (
        <ComposerModal
          editing={editingWin}
          onClose={() => { setComposerOpen(false); setEditingWin(null); }}
          onSaved={handleSaved}
        />
      )}
      {readerWin && (
        <ReaderModal
          key={readerWin.id}
          win={readerWin}
          boardMode={boardMode}
          onClose={() => setReaderWin(null)}
          onEdit={() => { setEditingWin(readerWin); setReaderWin(null); setComposerOpen(true); }}
          onShareImage={() => { setShareImageWin(readerWin); setReaderWin(null); }}
        />
      )}
      {!boardMode && shareImageWin && (
        <WinShareImageModal win={toShareImageWin(shareImageWin)} onClose={() => setShareImageWin(null)} />
      )}
      {!boardMode && importOpen && (
        <ImportCsvModal
          onClose={() => setImportOpen(false)}
          onImported={(count) => {
            setImportOpen(false);
            qc.invalidateQueries({ queryKey: ["wins-v2"] });
            if (count > 0) setConfettiTrigger((n) => n + 1);
          }}
        />
      )}
      <ConfettiBurst trigger={confettiTrigger} />
    </div>
  );
}

// Parses inline [#id] citations and renders them as clickable chips.
function renderAnswerWithCitations(
  text: string,
  winsById: Map<string, WinV2>,
  onOpenWin: (w: WinV2) => void,
): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\[#([a-zA-Z0-9_-]+)\]/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(<span key={`t${i++}`}>{text.slice(last, m.index)}</span>);
    const id = m[1];
    const w = winsById.get(id);
    if (w) {
      out.push(
        <button
          key={`c${i++}`}
          type="button"
          onClick={() => onOpenWin(w)}
          title={w.quote.slice(0, 120)}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded-md bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 text-[12px] font-medium align-baseline"
        >
          {w.author_name || w.account || "win"}
        </button>
      );
    } else {
      out.push(<span key={`x${i++}`} className="text-[hsl(var(--muted-foreground))]">[unknown]</span>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(<span key={`tend`}>{text.slice(last)}</span>);
  return out;
}

function AnswerCard({
  result, error, winsById, onClose, onOpenWin,
}: {
  result: AskResult | null;
  error: string | null;
  winsById: Map<string, WinV2>;
  onClose: () => void;
  onOpenWin: (w: WinV2) => void;
}) {
  const cited = result?.cited_ids.map((id) => winsById.get(id)).filter(Boolean) as WinV2[] | undefined;
  return (
    <div className="mb-6 rounded-2xl border border-[#8d6ae6]/20 bg-gradient-to-br from-[#faf6ff] via-white to-[#fff5fa] shadow-sm overflow-hidden">
      <div className="px-5 py-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#8d6ae6] to-[#ec67a0] text-white grid place-items-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-[#8d6ae6] font-medium mb-1">
            Ask AI
            {result && <span className="ml-2 normal-case text-[hsl(var(--muted-foreground))] tracking-normal">across {result.corpus_size} {result.corpus_size === 1 ? "win" : "wins"}</span>}
          </div>
          {result && (
            <div className="text-sm text-[hsl(var(--muted-foreground))] mb-2 truncate">
              "{result.question}"
            </div>
          )}
          {error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : result ? (
            <div className="font-serif text-[17px] leading-[1.65] text-[hsl(var(--foreground))] whitespace-pre-wrap">
              {renderAnswerWithCitations(result.answer, winsById, onOpenWin)}
            </div>
          ) : null}
          {cited && cited.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#8d6ae6]/15">
              <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] font-medium mb-2">
                Cited wins
              </div>
              <div className="flex flex-wrap gap-2">
                {cited.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => onOpenWin(w)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[hsl(var(--border))] hover:border-[#8d6ae6] hover:bg-[#8d6ae6]/5 text-[12px] transition max-w-full"
                  >
                    <QuoteIcon className="w-3 h-3 text-[#8d6ae6] shrink-0" />
                    <span className="truncate max-w-[280px]">
                      {w.author_name || w.account || w.quote.slice(0, 60)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-black/5 shrink-0"
          title="Dismiss"
        >
          <X className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
        </button>
      </div>
    </div>
  );
}

// ── List view ──────────────────────────────────────────────────────────────
function WinsListView({
  wins, boardMode = false, onOpen, onEdit, onShareImage,
}: {
  wins: WinV2[];
  boardMode?: boolean;
  onOpen: (w: WinV2) => void;
  onEdit: (w: WinV2) => void;
  onShareImage: (w: WinV2) => void;
}) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
      <ul className="divide-y divide-black/5">
        {wins.map((w) => (
          <WinListRow
            key={w.id}
            win={w}
            boardMode={boardMode}
            onOpen={() => onOpen(w)}
            onEdit={() => onEdit(w)}
            onShareImage={() => onShareImage(w)}
          />
        ))}
      </ul>
    </div>
  );
}

function WinListRow({
  win, boardMode = false, onOpen, onEdit, onShareImage,
}: {
  win: WinV2;
  boardMode?: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onShareImage: () => void;
}) {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const remove = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/wins-v2/${win.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wins-v2"] }),
  });
  const toggleStar = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/wins-v2/${win.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ starred: !win.starred }),
      });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wins-v2"] }),
  });
  async function copyText(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await copyWinText(win);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  }
  const date = new Date(win.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return (
    <li
      onClick={onOpen}
      className="group flex items-start gap-4 px-5 py-4 hover:bg-[hsl(var(--muted))]/40 cursor-pointer transition"
    >
      {boardMode ? (
        win.starred ? (
          <Star className="w-4 h-4 fill-amber-400 text-amber-500 mt-1 shrink-0" />
        ) : (
          <span className="w-4 h-4 mt-1 shrink-0" aria-hidden />
        )
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleStar.mutate(); }}
          className="p-1.5 -m-1.5 mt-0.5 rounded-md hover:bg-[hsl(var(--muted))]"
          title={win.starred ? "Unstar" : "Star"}
        >
          <Star className={`w-4 h-4 ${win.starred ? "fill-amber-400 text-amber-500" : "text-[hsl(var(--muted-foreground))]"}`} />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-serif text-[17px] leading-snug text-[hsl(var(--foreground))]">
          {truncate(win.quote, 240)}
        </div>
        <div className="mt-1.5 text-[12px] text-[hsl(var(--muted-foreground))] flex items-center gap-2 flex-wrap">
          {[win.author_name, win.account, win.platform && PLATFORM_LABEL[win.platform]]
            .filter(Boolean)
            .map((s, i, arr) => (
              <span key={i}>
                {s}{i < arr.length - 1 && <span className="mx-1.5 opacity-60">·</span>}
              </span>
            ))}
          <span className="opacity-60">·</span>
          <span>{date}</span>
          {(() => {
            const mediaItems = getMediaItems(win);
            if (!mediaItems.length) return null;
            const t = mediaItems[0].type;
            return (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[10px]">
                {t === "video" || t === "loom" || t === "youtube"
                  ? <><Video className="w-3 h-3" /> {t}</>
                  : <><ImageIcon className="w-3 h-3" /> {mediaItems.length > 1 ? `${mediaItems.length} images` : "media"}</>}
              </span>
            );
          })()}
          {win.ai_processed && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[#8d6ae6]">
              <Sparkles className="w-3 h-3" /> AI
            </span>
          )}
        </div>
      </div>
      <div className="shrink-0 self-center" onClick={(e) => e.stopPropagation()}>
        <VisibilityTag win={win} />
      </div>
      {!boardMode && <div className="flex items-center gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition">
        <button type="button" onClick={copyText} title={copied ? "Copied!" : "Copy as text"} className="p-1.5 rounded-md hover:bg-white">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Share2 className="w-3.5 h-3.5" />}
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onShareImage(); }} title="Share as image" className="p-1.5 rounded-md hover:bg-white">
          <ImageIcon className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit" className="p-1.5 rounded-md hover:bg-white">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (confirm("Delete this win? This can't be undone.")) remove.mutate();
          }}
          title="Delete"
          className="p-1.5 rounded-md hover:bg-red-50 text-red-500"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>}
    </li>
  );
}

// ── Import CSV modal ───────────────────────────────────────────────────────
interface ImportRow {
  quote: string;
  title: string | null;
  summary: string | null;
  author_name: string | null;
  account: string | null;
  platform: Platform | null;
  source_url: string | null;
  tags: string[];
  _include: boolean; // local-only — whether to commit this row
}

function ImportCsvModal({
  onClose, onImported,
}: {
  onClose: () => void;
  onImported: (count: number) => void;
}) {
  const [csv, setCsv] = useState("");
  const [phase, setPhase] = useState<"input" | "previewing" | "preview" | "committing">("input");
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [meta, setMeta] = useState<{ total_in_csv: number; skipped: number; notes: string; capped: boolean } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && phase !== "previewing" && phase !== "committing") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, phase]);

  async function pickFile(file: File) {
    setErr(null);
    if (file.size > 200_000) { setErr("CSV too large (max ~200KB)."); return; }
    const text = await file.text();
    setCsv(text);
  }

  async function runPreview() {
    setErr(null);
    if (!csv.trim()) { setErr("Paste a CSV or pick a file."); return; }
    setPhase("previewing");
    try {
      const res = await fetch("/api/wins-v2/import/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ csv }),
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      setRows((j.rows ?? []).map((r: Omit<ImportRow, "_include">) => ({ ...r, _include: true })));
      setMeta({ total_in_csv: j.total_in_csv, skipped: j.skipped, notes: j.notes, capped: !!j.capped });
      setPhase("preview");
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message.slice(0, 300) : String(e));
      setPhase("input");
    }
  }

  async function commit() {
    setErr(null);
    const toSend = rows
      .filter((r) => r._include)
      .map(({ _include: _i, ...rest }) => rest);
    if (toSend.length === 0) { setErr("Select at least one row to import."); return; }
    setPhase("committing");
    try {
      const res = await fetch("/api/wins-v2/import/commit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: toSend }),
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      onImported(j.created ?? toSend.length);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message.slice(0, 300) : String(e));
      setPhase("preview");
    }
  }

  function setRow(idx: number, patch: Partial<ImportRow>) {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function toggleAll(v: boolean) {
    setRows((rs) => rs.map((r) => ({ ...r, _include: v })));
  }

  const selectedCount = rows.filter((r) => r._include).length;

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-lg grid place-items-center p-4 overflow-y-auto"
      onClick={() => phase !== "previewing" && phase !== "committing" && onClose()}
    >
      <div
        className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-black/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#8d6ae6] to-[#ec67a0] text-white grid place-items-center">
              <FileSpreadsheet className="w-4 h-4" />
            </div>
            <div>
              <div className="font-serif text-xl">Import quotes from CSV</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
                Paste or upload — Felix auto-maps the columns. Max {50} rows per import.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={phase === "previewing" || phase === "committing"}
            className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {phase === "input" && (
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Export your sheet as CSV, then either pick the file or paste the contents below.
              Column names don't need to match — Felix figures out which column is the quote, who said it, and where it came from.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] text-sm text-[hsl(var(--muted-foreground))]"
              >
                <Upload className="w-4 h-4" /> Pick a .csv file
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) pickFile(f);
                }}
              />
              <span className="text-xs text-[hsl(var(--muted-foreground))]">or paste below ↓</span>
            </div>
            <textarea
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              rows={10}
              placeholder={'Quote,Author,Brokerage,Source\n"Felix found me a deal I would have missed",Sarah Lee,KW MAC,Facebook\n…'}
              className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm font-mono"
            />
            {err && <div className="text-sm text-red-600">{err}</div>}
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] text-sm">
                Cancel
              </button>
              <button
                type="button"
                onClick={runPreview}
                disabled={!csv.trim()}
                className="px-4 py-1.5 rounded-lg bg-[hsl(var(--primary))] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5" /> Preview with AI
              </button>
            </div>
          </div>
        )}

        {phase === "previewing" && (
          <div className="px-6 py-16 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#8d6ae6] mb-3" />
            <div className="text-sm text-[hsl(var(--muted-foreground))]">
              Felix is reading your CSV and mapping the columns…
            </div>
          </div>
        )}

        {phase === "preview" && (
          <>
            <div className="px-6 pt-4 pb-2 text-sm text-[hsl(var(--muted-foreground))] flex flex-wrap items-center gap-x-3 gap-y-1">
              {meta?.notes && <span className="italic">{meta.notes}</span>}
              <span>·</span>
              <span><strong className="text-[hsl(var(--foreground))]">{selectedCount}</strong> of {rows.length} selected</span>
              {meta?.capped && <span className="text-amber-700">· capped at 50 rows — split your CSV to import more</span>}
              <div className="flex-1" />
              <button onClick={() => toggleAll(true)} className="text-xs underline hover:no-underline">Select all</button>
              <button onClick={() => toggleAll(false)} className="text-xs underline hover:no-underline">Select none</button>
            </div>
            <div className="px-6 pb-4 max-h-[55vh] overflow-y-auto">
              <ul className="space-y-2">
                {rows.map((r, idx) => (
                  <li key={idx} className={`rounded-xl border ${r._include ? "border-[hsl(var(--primary))]/30 bg-white" : "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30"} px-3 py-3 flex gap-3`}>
                    <input
                      type="checkbox"
                      checked={r._include}
                      onChange={(e) => setRow(idx, { _include: e.target.checked })}
                      className="mt-1 rounded border-[hsl(var(--border))]"
                    />
                    <div className="flex-1 min-w-0">
                      <textarea
                        value={r.quote}
                        onChange={(e) => setRow(idx, { quote: e.target.value })}
                        rows={2}
                        className="w-full px-2 py-1 rounded border border-transparent hover:border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-[15px] font-serif leading-snug resize-y"
                      />
                      <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-2">
                        <input
                          value={r.author_name ?? ""}
                          onChange={(e) => setRow(idx, { author_name: e.target.value || null })}
                          placeholder="Author"
                          className="px-2 py-1 rounded border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-xs"
                        />
                        <input
                          value={r.account ?? ""}
                          onChange={(e) => setRow(idx, { account: e.target.value || null })}
                          placeholder="Account"
                          className="px-2 py-1 rounded border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-xs"
                        />
                        <select
                          value={r.platform ?? ""}
                          onChange={(e) => setRow(idx, { platform: (e.target.value || null) as Platform | null })}
                          className="px-2 py-1 rounded border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-xs bg-white"
                        >
                          <option value="">platform —</option>
                          {Object.entries(PLATFORM_LABEL).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-6 py-4 border-t border-black/5 flex items-center justify-between bg-[hsl(var(--muted))]/40 gap-3">
              {err ? <div className="text-sm text-red-600 truncate flex-1">{err}</div> : <div className="flex-1" />}
              <button type="button" onClick={() => setPhase("input")} className="px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] bg-white hover:bg-[hsl(var(--muted))] text-sm">
                Back
              </button>
              <button
                type="button"
                onClick={commit}
                disabled={selectedCount === 0}
                className="px-4 py-1.5 rounded-lg bg-[hsl(var(--primary))] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <Check className="w-3.5 h-3.5" /> Import {selectedCount} {selectedCount === 1 ? "win" : "wins"}
              </button>
            </div>
          </>
        )}

        {phase === "committing" && (
          <div className="px-6 py-16 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[hsl(var(--primary))] mb-3" />
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Importing into Felix…</div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Search input — also acts as the Ask AI trigger when the typed text looks
// like a question. Border turns purple, a soft halo pulses around it, and a
// "↵ Ask AI" hint chip appears on the right so the affordance is obvious
// without a separate button. Pressing Enter fires the ask.
function SearchInput({
  value, onChange, isQuestion, askLoading, onAsk,
}: {
  value: string;
  onChange: (v: string) => void;
  isQuestion: boolean;
  askLoading: boolean;
  onAsk: () => void;
}) {
  return (
    <div className="relative">
      <Search className="w-4 h-4 text-[hsl(var(--muted-foreground))] absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && isQuestion) onAsk(); }}
        placeholder="Search wins, or ask a question…"
        className={`w-full h-9 pl-9 ${isQuestion ? "pr-[112px]" : "pr-3"} rounded-lg bg-white border outline-none text-[14px] placeholder:text-[14px] placeholder:text-[hsl(var(--muted-foreground))] transition-colors ${
          isQuestion
            ? "border-[#8d6ae6] focus:border-[#8d6ae6] wins-search-glow"
            : "border-[hsl(var(--border))] focus:border-[hsl(var(--primary))]/60"
        }`}
      />
      {isQuestion && (
        <button
          type="button"
          onClick={onAsk}
          disabled={askLoading}
          title="Press Enter to ask AI"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 inline-flex items-center gap-1 px-2 rounded bg-[#8d6ae6]/10 text-[#8d6ae6] text-[11px] font-medium hover:bg-[#8d6ae6]/15 disabled:opacity-50"
        >
          {askLoading
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <CornerDownLeft className="w-3 h-3" />}
          Ask AI
        </button>
      )}
    </div>
  );
}

function EmptyState({ onAdd, hasFilter, boardMode = false }: { onAdd: () => void; hasFilter: boolean; boardMode?: boolean }) {
  return (
    <div className="py-24 text-center">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[hsl(var(--muted))] grid place-items-center mb-4">
        <QuoteIcon className="w-6 h-6 text-[hsl(var(--muted-foreground))]" />
      </div>
      <div className="font-serif text-2xl mb-1">
        {hasFilter ? "No wins match that" : "No wins yet"}
      </div>
      <p className="text-[hsl(var(--muted-foreground))] max-w-sm mx-auto mb-6">
        {hasFilter
          ? "Try a broader search or clear the filters."
          : boardMode
            ? "Check back soon — the wall is just getting started."
            : "Drop in a screenshot of a client praising Felix and we'll extract the quote for you."}
      </p>
      {!hasFilter && !boardMode && (
        <button
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[hsl(var(--primary))] text-white text-sm font-medium hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> Add the first win
        </button>
      )}
    </div>
  );
}

// External/Internal visibility. Read-only badge for any authed Fello user; a
// segmented toggle for the allowlisted taggers (canTagVisibility). Renders
// nothing for anonymous visitors — the public board already shows external-only.
function VisibilityTag({ win }: { win: WinV2 }) {
  const { isAuthenticated, isLoading, canTagVisibility, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  if (isLoading || !isAuthenticated) return null;
  const vis = winVisibility(win);
  const canReview = canTagVisibility || isAdmin;
  const showReviewBtn = canReview && vis === "internal" && !win.reviewed;
  const patchWin = async (body: Record<string, unknown>, e: React.MouseEvent) => {
    e.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/wins-v2/${win.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.text()).slice(0, 200));
      await qc.invalidateQueries({ queryKey: ["wins-v2"] });
    } catch (err) {
      alert(err instanceof Error ? err.message : "Couldn't update");
    } finally {
      setSaving(false);
    }
  };
  if (!canTagVisibility) {
    return vis === "external" ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap">
        <Globe className="w-3 h-3" /> External
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap">
        <Lock className="w-3 h-3" /> Internal
      </span>
    );
  }
  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <div
        className="inline-flex items-center rounded-full border border-[hsl(var(--border))] bg-white p-0.5 shrink-0"
        title="Who can see this win"
      >
        <button
          type="button"
          disabled={saving}
          onClick={(e) => patchWin({ visibility: "external" }, e)}
          className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-medium transition disabled:opacity-50 ${vis === "external" ? "bg-emerald-600 text-white" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
        >
          <Globe className="w-3 h-3" /> External
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={(e) => patchWin({ visibility: "internal" }, e)}
          className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-medium transition disabled:opacity-50 ${vis === "internal" ? "bg-slate-700 text-white" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
        >
          <Lock className="w-3 h-3" /> Internal
        </button>
        {saving && <Loader2 className="w-3 h-3 animate-spin mx-1 text-[hsl(var(--muted-foreground))]" />}
      </div>
      {showReviewBtn && (
        <button
          type="button"
          disabled={saving}
          onClick={(e) => patchWin({ reviewed: true }, e)}
          title="Keep as internal and dismiss from review"
          className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition disabled:opacity-50"
        >
          <Check className="w-3 h-3" /> Reviewed
        </button>
      )}
    </div>
  );
}

// ── Card with portaled ⋯ menu ──────────────────────────────────────────────
function WinCard({
  win, boardMode = false, onOpen, onEdit, onShareImage,
}: {
  win: WinV2;
  boardMode?: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onShareImage: () => void;
}) {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const remove = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/wins-v2/${win.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wins-v2"] }),
  });
  const toggleStar = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/wins-v2/${win.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ starred: !win.starred }),
      });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wins-v2"] }),
  });

  const [copied, setCopied] = useState(false);
  async function onCopyText(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await copyWinText(win);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  }

  const mediaItems = getMediaItems(win);
  const hero = mediaItems[0];
  return (
    <article
      onClick={onOpen}
      className="group break-inside-avoid mb-5 bg-white rounded-2xl border border-black/5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)] transition cursor-pointer overflow-hidden"
    >
      {hero && (
        <div className="relative">
          <MediaView url={hero.url} type={hero.type} title={win.title} muxAssetId={hero.mux_asset_id} muxPlaybackId={hero.mux_playback_id} />
          {mediaItems.length > 1 && (
            <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white">
              <ImageIcon className="w-3 h-3" /> 1/{mediaItems.length}
            </span>
          )}
        </div>
      )}
      <div className="p-5">
        <p className="font-serif text-[19px] leading-[1.55] text-[hsl(var(--foreground))]">
          {!hero && <QuoteIcon className="inline w-5 h-5 text-[hsl(var(--primary))] -mt-1 mr-1.5 opacity-60" />}
          {truncate(win.quote, 280)}
        </p>
        {win.title && (
          <div className="mt-2 text-[12px] text-[hsl(var(--muted-foreground))] leading-snug">
            {win.title}
          </div>
        )}
        {isAuthenticated && (
          <div className="mt-3">
            <VisibilityTag win={win} />
          </div>
        )}
        <div className="mt-4 flex items-center justify-between text-[12px] text-[hsl(var(--muted-foreground))]">
          <div className="truncate">
            {[win.author_name, win.account, win.platform && PLATFORM_LABEL[win.platform]]
              .filter(Boolean)
              .join(" · ")}
          </div>
          {boardMode ? (
            win.starred && (
              <Star className="w-4 h-4 fill-amber-400 text-amber-500 shrink-0" />
            )
          ) : (
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleStar.mutate(); }}
                title={win.starred ? "Unstar" : "Star"}
                className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"
              >
                <Star className={`w-4 h-4 ${win.starred ? "fill-amber-400 text-amber-500" : ""}`} />
              </button>
              <button
                type="button"
                onClick={onCopyText}
                title={copied ? "Copied!" : "Copy as text"}
                className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Share2 className="w-3.5 h-3.5" />}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onShareImage(); }}
                title="Share as image"
                className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"
              >
                <ImageIcon className="w-3.5 h-3.5" />
              </button>
              <CardMenu
                onEdit={onEdit}
                onDelete={() => {
                  if (confirm("Delete this win? This can't be undone.")) remove.mutate();
                }}
              />
            </div>
          )}
        </div>
        {win.ai_processed && (
          <div className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#8d6ae6]">
            <Sparkles className="w-3 h-3" /> AI extracted
          </div>
        )}
      </div>
    </article>
  );
}

function CardMenu({
  onEdit, onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    // Right-aligned to the button, opening downward. Falls back upward if near bottom.
    const menuWidth = 180;
    const left = Math.max(8, r.right - menuWidth);
    const top = r.bottom + 6;
    setPos({ top, left });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("scroll", close, true);
    document.addEventListener("click", close);
    return () => {
      document.removeEventListener("scroll", close, true);
      document.removeEventListener("click", close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        title="More"
        className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {open && pos && createPortal(
        <div
          style={{ position: "fixed", top: pos.top, left: pos.left, width: 160 }}
          className="z-[60] rounded-xl border border-black/5 bg-white shadow-xl py-1 text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onEdit(); }} className="flex w-full items-center gap-2 px-3 py-2 hover:bg-[hsl(var(--muted))]">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
          <div className="my-1 h-px bg-black/5" />
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete(); }} className="flex w-full items-center gap-2 px-3 py-2 hover:bg-red-50 text-red-600">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>,
        document.body
      )}
    </>
  );
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

// ── Reader modal ────────────────────────────────────────────────────────────
function ReaderModal({
  win, boardMode = false, onClose, onEdit, onShareImage,
}: {
  win: WinV2;
  boardMode?: boolean;
  onClose: () => void;
  onEdit: () => void;
  onShareImage: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  async function onCopyText() {
    try {
      await copyWinText(win);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  }
  async function onCopyLink() {
    try {
      await navigator.clipboard.writeText(shareUrlForWin(win));
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
    } catch { /* noop */ }
  }
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-lg flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {(() => {
          const mediaItems = getMediaItems(win);
          if (!mediaItems.length) return null;
          return (
            <div className="shrink-0 bg-[hsl(var(--muted))] flex items-center justify-center">
              <WinGallery items={mediaItems} title={win.title} eager />
            </div>
          );
        })()}
        <div className="p-8 overflow-y-auto">
          <p className="font-serif text-[24px] leading-[1.55] max-w-[58ch] whitespace-pre-wrap">
            {win.quote}
          </p>
          {win.title && (
            <p className="mt-5 text-[13px] text-[hsl(var(--muted-foreground))] leading-snug">
              {win.title}
            </p>
          )}
          {win.summary && win.summary !== win.title && (
            <p className="mt-2 text-[13px] text-[hsl(var(--muted-foreground))]">{win.summary}</p>
          )}
          {win.tags?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-1.5">
              {win.tags.map((t) => (
                <span key={t} className="px-2 py-0.5 rounded-full bg-[hsl(var(--muted))] text-[11px]">#{t}</span>
              ))}
            </div>
          )}
          <div className="mt-6 pt-5 border-t border-black/5 flex items-center justify-between text-sm text-[hsl(var(--muted-foreground))] gap-4 flex-wrap">
            <div className="truncate">
              {[win.author_name, win.account, win.platform && PLATFORM_LABEL[win.platform]]
                .filter(Boolean)
                .join(" · ")}
              {win.source_url && (
                <a href={win.source_url} target="_blank" rel="noopener" className="ml-2 inline-flex items-center gap-1 text-[hsl(var(--primary))] hover:underline">
                  source <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Copy link is useful on every surface — including the public
                  board, where it's the primary share action. */}
              <button onClick={onCopyLink} className="px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] inline-flex items-center gap-1.5 text-sm">
                {linkCopied ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> Link copied</> : <><Link2 className="w-3.5 h-3.5" /> Copy link</>}
              </button>
              {!boardMode && (
                <>
                  <button onClick={onCopyText} className="px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] inline-flex items-center gap-1.5 text-sm">
                    {copied ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> Copied</> : <><Share2 className="w-3.5 h-3.5" /> Copy as text</>}
                  </button>
                  <button onClick={onShareImage} className="px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] inline-flex items-center gap-1.5 text-sm">
                    <ImageIcon className="w-3.5 h-3.5" /> Share as image
                  </button>
                  <button onClick={onEdit} className="px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] inline-flex items-center gap-1.5 text-sm">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                </>
              )}
              <button onClick={onClose} className="px-3 py-1.5 rounded-lg bg-[hsl(var(--muted))] hover:bg-[hsl(var(--accent))] text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Composer ────────────────────────────────────────────────────────────────
type Tab = "ai" | "manual" | "link" | "bulk";

interface ComposerState {
  quote: string;
  title: string;
  summary: string;
  platform: Platform | "";
  source_url: string;
  account: string;
  author_name: string;
  media: MediaItem[];
  tags: string;
  starred: boolean;
  ai_processed: boolean;
}

const emptyForm: ComposerState = {
  quote: "", title: "", summary: "", platform: "", source_url: "",
  account: "", author_name: "", media: [],
  tags: "", starred: false, ai_processed: false,
};

function fromWin(w: WinV2): ComposerState {
  return {
    quote: w.quote,
    title: w.title ?? "",
    summary: w.summary ?? "",
    platform: (w.platform ?? "") as Platform | "",
    source_url: w.source_url ?? "",
    account: w.account ?? "",
    author_name: w.author_name ?? "",
    media: getMediaItems(w),
    tags: (w.tags ?? []).join(", "),
    starred: w.starred,
    ai_processed: w.ai_processed,
  };
}

function ComposerModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: WinV2 | null;
  onClose: () => void;
  onSaved: (opts: { wasNew: boolean }) => void;
}) {
  const [tab, setTab] = useState<Tab>(editing ? "manual" : "ai");
  const [form, setForm] = useState<ComposerState>(editing ? fromWin(editing) : emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [aiNotice, setAiNotice] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  function setField<K extends keyof ComposerState>(k: K, v: ComposerState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    setErr(null);
    if (!form.quote.trim() && form.media.length === 0) {
      setErr("Add a quote or media before posting.");
      return;
    }
    setSaving(true);
    try {
      const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      const body = {
        quote: form.quote.trim() || (form.title || "(media only)"),
        title: form.title.trim() || null,
        summary: form.summary.trim() || null,
        platform: form.platform || null,
        source_url: form.source_url.trim() || null,
        account: form.account.trim() || null,
        author_name: form.author_name.trim() || null,
        // The server mirrors media[0] into media_url/media_type for back-compat.
        media: form.media,
        tags,
        starred: form.starred,
        ai_processed: form.ai_processed,
      };
      const url = editing ? `/api/wins-v2/${editing.id}` : "/api/wins-v2";
      const method = editing ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const t = await res.text();
        let msg = t;
        try { msg = JSON.parse(t).error || t; } catch { /* not json */ }
        throw new Error(msg || `${res.status}`);
      }
      onSaved({ wasNew: !editing });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg.slice(0, 300));
      setSaving(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-lg grid place-items-center p-4 overflow-y-auto" onClick={() => !saving && onClose()}>
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden my-8" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-black/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-[hsl(var(--primary))] text-white grid place-items-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="font-serif text-xl">{editing ? "Edit win" : "Add a Felix win"}</div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!editing && (
          <div className="px-6 pt-4 flex items-center gap-1.5">
            <TabBtn active={tab === "ai"} onClick={() => setTab("ai")} icon={<Sparkles className="w-3.5 h-3.5" />}>
              Screenshot → AI extract
            </TabBtn>
            <TabBtn active={tab === "manual"} onClick={() => setTab("manual")} icon={<Pencil className="w-3.5 h-3.5" />}>
              Type it in
            </TabBtn>
            <TabBtn active={tab === "link"} onClick={() => setTab("link")} icon={<Link2 className="w-3.5 h-3.5" />}>
              Loom / video link
            </TabBtn>
            <TabBtn active={tab === "bulk"} onClick={() => setTab("bulk")} icon={<Video className="w-3.5 h-3.5" />}>
              Bulk videos
            </TabBtn>
          </div>
        )}

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {tab === "ai" && !editing && (
            <ExtractFlow
              onResult={(r) => {
                const gotQuote = typeof r.quote === "string" && r.quote.trim().length > 0;
                setForm((f) => ({
                  ...f,
                  quote: r.quote || f.quote,
                  title: r.summary || f.title,
                  summary: r.summary || f.summary,
                  author_name: r.author_name || f.author_name,
                  platform: (r.platform as Platform) || f.platform,
                  account: r.account || f.account,
                  source_url: r.source_url_if_visible || f.source_url,
                  media: r.media?.length ? r.media : f.media,
                  ai_processed: gotQuote,
                }));
                if (!gotQuote) {
                  setAiNotice(r.media?.length && r.media.every((m) => m.type === "video")
                    ? "Felix couldn't find a clear praise quote in this video (or its captions weren't ready). Your video is saved — please type the quote and details below."
                    : "Felix couldn't find a clear praise quote in this screenshot — likely a chat thread or UI without obvious testimonial wording. The screenshot is saved; please type the quote yourself below.");
                } else {
                  setAiNotice(null);
                }
                setTab("manual");
              }}
            />
          )}

          {tab === "bulk" && !editing && (
            <BulkVideoTab onBusyChange={setSaving} onDone={() => onSaved({ wasNew: true })} />
          )}

          {tab === "link" && !editing && (
            <LinkTab
              onLink={(url) => {
                const detected = normalizeUrl(url);
                const mt: MediaType =
                  detected.kind === "loom" || detected.kind === "youtube" || detected.kind === "video" || detected.kind === "image"
                    ? detected.kind : "image";
                setForm((f) => ({ ...f, media: [...f.media, { url, type: mt }] }));
                setTab("manual");
              }}
            />
          )}

          {tab === "manual" && (
            <>
              {aiNotice && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-start gap-2.5 text-[13px] text-amber-900">
                  <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1 leading-snug">{aiNotice}</div>
                  <button
                    type="button"
                    onClick={() => setAiNotice(null)}
                    className="p-0.5 rounded hover:bg-amber-100 shrink-0"
                    title="Dismiss"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <ManualForm form={form} setField={setField} />
            </>
          )}
        </div>

        {tab === "manual" && (
          <div className="px-6 py-4 border-t border-black/5 flex items-center justify-between bg-[hsl(var(--muted))]/40">
            {err ? (
              <div className="text-sm text-red-600 truncate mr-3">{err}</div>
            ) : <div />}
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} disabled={saving} className="px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] bg-white hover:bg-[hsl(var(--muted))] text-sm">
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || (!form.quote.trim() && form.media.length === 0)}
                className="px-4 py-1.5 rounded-lg bg-[hsl(var(--primary))] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {editing ? "Save changes" : "Post win"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Bulk video upload ───────────────────────────────────────────────────────
// Drop many videos at once: each becomes its OWN win. They upload straight to
// Mux (two at a time), Felix reads each transcript, and the results are listed
// as editable drafts to review before "Post all".
interface BulkRow {
  id: string;
  file: File;
  phase: "queued" | "uploading" | "watching" | "ready" | "failed" | "posting" | "posted" | "post_failed";
  progress: number;
  error?: string;
  media?: MediaItem;
  quote: string;
  summary: string;
  author_name: string;
  account: string;
  ai: boolean;
}

const BULK_CONCURRENCY = 2;

function BulkVideoTab({ onBusyChange, onDone }: { onBusyChange: (busy: boolean) => void; onDone: () => void }) {
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const queueRef = useRef<string[]>([]);
  const filesRef = useRef(new Map<string, File>());
  const activeRef = useRef(0);

  const patch = useCallback((id: string, p: Partial<BulkRow>) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));
  }, []);

  const processRow = useCallback(async (id: string) => {
    const file = filesRef.current.get(id);
    if (!file) return;
    patch(id, { phase: "uploading", progress: 0 });
    try {
      const item = await uploadVideoToMux(file, (l) => patch(id, { progress: Math.round((l / file.size) * 100) }));
      if (!item?.mux_asset_id) throw new Error("Video processing isn't configured on the server.");
      patch(id, { media: item, phase: "watching", progress: 100 });
      let ex: ExtractResult | null = null;
      try { ex = await extractFromMuxVideo(item.mux_asset_id); } catch (e) { console.warn("[wins] bulk extract failed:", e); }
      patch(id, {
        phase: "ready",
        quote: ex?.quote ?? "",
        summary: ex?.summary ?? "",
        author_name: ex?.author_name ?? "",
        account: ex?.account ?? "",
        ai: !!ex?.quote?.trim(),
      });
    } catch (e: unknown) {
      patch(id, { phase: "failed", error: e instanceof Error ? e.message : String(e) });
    }
  }, [patch]);

  const pump = useCallback(() => {
    while (activeRef.current < BULK_CONCURRENCY && queueRef.current.length) {
      const id = queueRef.current.shift()!;
      activeRef.current++;
      processRow(id).finally(() => { activeRef.current--; pump(); });
    }
  }, [processRow]);

  function addFiles(files: File[]) {
    const videos = files.filter((f) => f.type.startsWith("video/"));
    if (!videos.length) return;
    const fresh: BulkRow[] = videos.map((file) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      filesRef.current.set(id, file);
      queueRef.current.push(id);
      return { id, file, phase: "queued", progress: 0, quote: "", summary: "", author_name: "", account: "", ai: false };
    });
    setRows((rs) => [...rs, ...fresh]);
    pump();
  }

  function removeRow(id: string) {
    queueRef.current = queueRef.current.filter((q) => q !== id);
    filesRef.current.delete(id);
    setRows((rs) => rs.filter((r) => r.id !== id));
  }

  const working = rows.some((r) => ["queued", "uploading", "watching", "posting"].includes(r.phase));
  useEffect(() => { onBusyChange(working || posting); }, [working, posting, onBusyChange]);

  const postable = rows.filter((r) => r.phase === "ready" || r.phase === "post_failed");

  async function postAll() {
    setPosting(true);
    let failures = 0;
    for (const r of postable) {
      patch(r.id, { phase: "posting", error: undefined });
      try {
        const res = await fetch("/api/wins-v2", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            quote: r.quote.trim() || r.summary.trim() || "(video win — see attached video)",
            title: r.summary.trim() || null,
            summary: r.summary.trim() || null,
            platform: null,
            account: r.account.trim() || null,
            author_name: r.author_name.trim() || null,
            media: r.media ? [r.media] : [],
            tags: [],
            starred: false,
            ai_processed: r.ai,
          }),
        });
        if (!res.ok) {
          const t = await res.text();
          let msg = t;
          try { msg = JSON.parse(t).error || t; } catch { /* not json */ }
          throw new Error(msg || String(res.status));
        }
        patch(r.id, { phase: "posted" });
      } catch (e: unknown) {
        failures++;
        patch(r.id, { phase: "post_failed", error: (e instanceof Error ? e.message : String(e)).slice(0, 200) });
      }
    }
    setPosting(false);
    if (!failures) onDone();
  }

  const statusText = (r: BulkRow) => {
    switch (r.phase) {
      case "queued": return "Waiting…";
      case "uploading": return `Uploading… ${r.progress}%`;
      case "watching": return "AI analysing…";
      case "ready": return r.ai ? "Ready — AI extracted" : "Ready — add a quote (none found)";
      case "posting": return "Posting…";
      case "posted": return "Posted";
      case "post_failed": return r.error ?? "Post failed";
      default: return r.error ?? "Failed";
    }
  };

  const fieldCls = "w-full px-2.5 py-1.5 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm";

  return (
    <div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
        Drop or pick several videos at once. Each one becomes its own win — Felix reads the captions of every video, then you review the list and post them all together.
      </p>
      <div
        onDrop={(e) => { e.preventDefault(); addFiles(Array.from(e.dataTransfer.files ?? [])); }}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-[hsl(var(--border))] rounded-2xl p-5 text-center cursor-pointer hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 transition"
      >
        <div className="text-sm font-medium">Drop videos here, or click to choose</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">MP4, MOV, WebM · any size · as many as you like</div>
        <input
          ref={fileRef}
          type="file"
          accept="video/*"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(Array.from(e.target.files ?? [])); e.target.value = ""; }}
        />
      </div>

      {rows.length > 0 && (() => {
        const count = (...ph: BulkRow["phase"][]) => rows.filter((r) => ph.includes(r.phase)).length;
        const finished = count("ready", "posted", "post_failed", "failed", "posting");
        const pct = Math.round((finished / rows.length) * 100);
        return (
          <div className="mt-4 rounded-xl bg-[hsl(var(--muted))]/60 px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="font-medium">Queue · {rows.length} video{rows.length === 1 ? "" : "s"}</span>
              <span className="text-[hsl(var(--muted-foreground))]">{count("queued")} waiting</span>
              <span className="text-[hsl(var(--muted-foreground))]">{count("uploading")} uploading</span>
              <span className="text-[#8d6ae6]">{count("watching")} in AI analysis</span>
              <span className="text-emerald-700">{count("ready", "posting", "posted", "post_failed")} ready</span>
              {count("failed") > 0 && <span className="text-red-600">{count("failed")} failed</span>}
            </div>
            <div className="mt-2 h-1 rounded-full bg-black/10 overflow-hidden">
              <div className="h-full bg-[hsl(var(--primary))] transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })()}

      {rows.length > 0 && (
        <ul className="mt-3 space-y-3">
          {rows.map((r) => {
            const locked = r.phase !== "ready" && r.phase !== "post_failed";
            return (
              <li key={r.id} className="rounded-xl border border-[hsl(var(--border))] p-3 flex gap-3">
                <div className="w-20 h-20 shrink-0 rounded-lg bg-black overflow-hidden grid place-items-center">
                  {r.media?.mux_playback_id ? (
                    <img src={`https://image.mux.com/${r.media.mux_playback_id}/thumbnail.jpg?width=160&height=160&fit_mode=smartcrop`}
                      alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Loader2 className="w-4 h-4 animate-spin text-white/70" />
                  )}
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="truncate font-medium">{r.file.name}</span>
                    <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${
                      r.phase === "failed" || r.phase === "post_failed" ? "bg-red-50 text-red-700"
                      : r.phase === "posted" ? "bg-emerald-50 text-emerald-700"
                      : r.phase === "watching" ? "bg-violet-50 text-[#8d6ae6]"
                      : r.phase === "ready" ? "bg-emerald-50 text-emerald-700"
                      : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                    }`}>
                      {r.phase === "watching" && <Sparkles className="w-3 h-3 animate-pulse" />}
                      {(r.phase === "uploading" || r.phase === "posting") && <Loader2 className="w-3 h-3 animate-spin" />}
                      {(r.phase === "ready" || r.phase === "posted") && <Check className="w-3 h-3" />}
                      {statusText(r)}
                    </span>
                    {r.phase !== "posted" && r.phase !== "posting" && (
                      <button type="button" onClick={() => removeRow(r.id)} className="ml-auto p-1 rounded hover:bg-[hsl(var(--muted))]" title="Remove">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {r.phase === "uploading" && (
                    <div className="h-1 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                      <div className="h-full bg-[hsl(var(--primary))] transition-all" style={{ width: `${r.progress}%` }} />
                    </div>
                  )}
                  {!locked && (
                    <>
                      <textarea value={r.quote} rows={2} placeholder="Quote"
                        onChange={(e) => patch(r.id, { quote: e.target.value })}
                        className={`${fieldCls} font-serif`} />
                      <input value={r.summary} placeholder="Short summary / title"
                        onChange={(e) => patch(r.id, { summary: e.target.value })} className={fieldCls} />
                      <div className="grid grid-cols-2 gap-2">
                        <input value={r.author_name} placeholder="Author"
                          onChange={(e) => patch(r.id, { author_name: e.target.value })} className={fieldCls} />
                        <input value={r.account} placeholder="Account / Brokerage"
                          onChange={(e) => patch(r.id, { account: e.target.value })} className={fieldCls} />
                      </div>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {rows.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            {rows.filter((r) => r.phase === "posted").length} posted · {postable.length} ready{working && !posting ? " · processing…" : ""}
          </div>
          <button
            type="button"
            onClick={postAll}
            disabled={posting || working || !postable.length}
            className="px-4 py-1.5 rounded-lg bg-[hsl(var(--primary))] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {rows.some((r) => r.phase === "post_failed") ? "Retry failed" : `Post ${postable.length} win${postable.length === 1 ? "" : "s"}`}
          </button>
        </div>
      )}
    </div>
  );
}

function TabBtn({
  active, onClick, icon, children,
}: {
  active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition ${
        active
          ? "bg-[hsl(var(--primary))] text-white"
          : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
      }`}
    >
      {icon}{children}
    </button>
  );
}

// ── Link tab ────────────────────────────────────────────────────────────────
function LinkTab({ onLink }: { onLink: (url: string) => void }) {
  const [v, setV] = useState("");
  const detected = v ? normalizeUrl(v) : null;
  return (
    <div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
        Paste a Loom or YouTube URL, or any sharing link (Drive, Figma, Gong, etc). Felix figures out how to render it.
      </p>
      <div className="flex items-center gap-2">
        <input
          autoFocus
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder="https://www.loom.com/share/…"
          className="flex-1 px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm"
        />
        <button
          type="button"
          onClick={() => onLink(v.trim())}
          disabled={!v.trim()}
          className="px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          Use this link
        </button>
      </div>
      {detected && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Detected as: {detected.kind}</div>
          {detected.kind === "loom" || detected.kind === "youtube"
            ? <div className="rounded-xl overflow-hidden border border-[hsl(var(--border))]"><EmbedFacade kind={detected.kind} url={detected.url} /></div>
            : detected.kind === "image"
              ? <img src={detected.url} alt="" className="max-h-60 rounded-lg border border-[hsl(var(--border))]" />
              : detected.kind === "video"
                ? <video src={detected.url} controls className="max-h-60 rounded-lg bg-black" />
                : detected.kind === "link"
                  ? <div className="rounded-xl overflow-hidden border border-[hsl(var(--border))]"><LinkCard url={detected.url} /></div>
                  : <div className="text-xs text-[hsl(var(--muted-foreground))]">Not a recognized media link.</div>
          }
        </div>
      )}
    </div>
  );
}

// ── AI Extract flow ────────────────────────────────────────────────────────
interface ExtractResult {
  quote: string;
  author_name: string | null;
  platform: string | null;
  account: string | null;
  summary: string | null;
  source_url_if_visible: string | null;
  media?: MediaItem[];
}

// Wait for Mux to finish processing + auto-captioning, then extract a quote from
// the transcript. Resolves null (→ manual form) on timeout, errored asset, or a
// missing captions track; never throws on "not available", only on hard errors.
async function extractFromMuxVideo(assetId: string): Promise<ExtractResult | null> {
  const deadline = Date.now() + 3 * 60 * 1000;
  let readySince: number | null = null;
  while (Date.now() < deadline) {
    const r = await fetch(`/api/wins-v2/mux/${assetId}`);
    if (!r.ok) return null;
    const st: { status: string; text_tracks: { id: string; status: string }[] } = await r.json();
    if (st.status === "errored") return null;
    if (st.status === "ready") {
      readySince ??= Date.now();
      if (st.text_tracks.some((t) => t.status === "ready")) {
        const ex = await fetch(`/api/wins-v2/mux/${assetId}/extract`, { method: "POST" });
        if (!ex.ok) throw new Error(await ex.text());
        return await ex.json();
      }
      // Captions not generated (plan/silent video): give up shortly after ready.
      if (st.text_tracks.some((t) => t.status === "errored") || Date.now() - readySince > 45_000) return null;
    }
    await new Promise((res) => setTimeout(res, 3000));
  }
  return null;
}

function ExtractFlow({ onResult }: { onResult: (r: ExtractResult) => void }) {
  const [phase, setPhase] = useState<"idle" | "uploading" | "extracting" | "watching" | "done" | "error">("idle");
  const [errMsg, setErrMsg] = useState("");
  const [previews, setPreviews] = useState<{ src: string; video: boolean }[]>([]);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      if (["uploading", "extracting", "watching"].includes(phaseRef.current)) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of items) {
        if (item.kind === "file" && (item.type.startsWith("image/") || item.type.startsWith("video/"))) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length) {
        e.preventDefault();
        handleFiles(files);
      }
    }
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFiles(files: File[]) {
    setErrMsg("");
    const accepted = files.filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"));
    const images = accepted.filter((f) => f.type.startsWith("image/"));
    if (!accepted.length) {
      setErrMsg("Please drop images (PNG, JPG, WebP, HEIC) or videos (MP4, MOV, WebM).");
      return;
    }
    const tooBig = accepted.find((f) => f.type.startsWith("image/") && f.size > 10 * 1024 * 1024);
    if (tooBig) {
      setErrMsg(`${tooBig.name} is too large (images up to 10MB).`);
      return;
    }
    setPreviews(accepted.map((f) => ({ src: URL.createObjectURL(f), video: f.type.startsWith("video/") })));
    setPhase("uploading");
    setProgress(0);
    let media: MediaItem[] = [];
    try {
      media = await uploadWinMedia(accepted, setProgress);
    } catch (e: unknown) {
      setPhase("error");
      setErrMsg("Upload failed: " + (e instanceof Error ? e.message : String(e)));
      return;
    }
    // AI reads only the FIRST screenshot for the quote/attribution; all media
    // ride along in the gallery. A video-only post is read through Mux's
    // auto-generated transcript instead; if that isn't available it falls
    // through to the manual form with whatever Felix could get.
    const emptyExtract: ExtractResult = { quote: "", author_name: null, platform: null, account: null, summary: null, source_url_if_visible: null };
    if (!images.length) {
      const muxVideo = media.find((m) => m.mux_asset_id);
      if (!muxVideo?.mux_asset_id) {
        setPhase("done");
        onResult({ ...emptyExtract, media });
        return;
      }
      setPhase("watching");
      try {
        const ex = await extractFromMuxVideo(muxVideo.mux_asset_id);
        setPhase("done");
        onResult({ ...(ex ?? emptyExtract), media });
      } catch (e: unknown) {
        console.warn("[wins] video extract failed:", e);
        setPhase("done");
        onResult({ ...emptyExtract, media });
      }
      return;
    }
    setPhase("extracting");
    try {
      const fd = new FormData();
      fd.append("file", images[0]);
      const exRes = await fetch("/api/wins-v2/extract", { method: "POST", body: fd });
      if (!exRes.ok) throw new Error(await exRes.text());
      const ex: ExtractResult = await exRes.json();
      setPhase("done");
      onResult({ ...ex, media });
    } catch (e: unknown) {
      setPhase("error");
      setErrMsg("AI extract failed: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length) handleFiles(files);
  }

  return (
    <div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
        Drop, paste, or upload one or more screenshots (or videos) of a Facebook comment, X post, LinkedIn DM, email, Slack message, or any praise from a client.
        Felix reads the first one for the quote and attribution — every image is kept as a gallery you can page through. Review and edit before posting.
      </p>
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-[hsl(var(--border))] rounded-2xl p-6 text-center cursor-pointer hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 transition"
      >
        {previews.length ? (
          <div className="flex flex-wrap items-center justify-center gap-2">
            {previews.map((p, i) => p.video
              ? <video key={i} src={p.src} muted className="max-h-32 rounded-lg bg-black" />
              : <img key={i} src={p.src} alt={`preview ${i + 1}`} className="max-h-32 rounded-lg" />
            )}
          </div>
        ) : (
          <>
            <div className="w-10 h-10 mx-auto rounded-xl bg-[hsl(var(--muted))] grid place-items-center mb-2">
              <ImageIcon className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
            </div>
            <div className="text-sm font-medium">
              Paste <kbd className="px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[11px] font-mono mx-0.5">⌘V</kbd>, drop files, or click to upload
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">PNG, JPG, WebP, HEIC up to 10MB · MP4, MOV, WebM any size · multiple allowed</div>
          </>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) handleFiles(files);
          }}
        />
      </div>
      <div className="mt-4 min-h-[28px] text-sm">
        {phase === "uploading" && (
          <div className="inline-flex items-center gap-2 text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" /> Uploading {previews.length > 1 ? "files" : "file"}… {progress}%</div>
        )}
        {phase === "extracting" && (
          <div className="inline-flex items-center gap-2 text-[#8d6ae6]"><Sparkles className="w-4 h-4 animate-pulse" /> Felix is reading the comment…</div>
        )}
        {phase === "watching" && (
          <div className="inline-flex items-center gap-2 text-[#8d6ae6]"><Sparkles className="w-4 h-4 animate-pulse" /> Felix is watching the video… this can take a minute or two.</div>
        )}
        {phase === "done" && (
          <div className="inline-flex items-center gap-2 text-emerald-700"><Check className="w-4 h-4" /> Extracted — review and post.</div>
        )}
        {phase === "error" && (
          <div className="text-red-600">{errMsg}</div>
        )}
      </div>
    </div>
  );
}

// ── Manual form ─────────────────────────────────────────────────────────────
function ManualForm({
  form, setField,
}: {
  form: ComposerState;
  setField: <K extends keyof ComposerState>(k: K, v: ComposerState[K]) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState("");
  const [upProgress, setUpProgress] = useState(0);

  async function uploadFiles(files: File[]) {
    if (!files.length) return;
    setUpErr("");
    setUploading(true);
    setUpProgress(0);
    try {
      const added = await uploadWinMedia(files, setUpProgress);
      setField("media", [...form.media, ...added]);
    } catch (e: unknown) {
      setUpErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  function removeMediaAt(i: number) {
    setField("media", form.media.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-4">
      <Field label="Quote">
        <textarea
          value={form.quote}
          onChange={(e) => setField("quote", e.target.value)}
          rows={4}
          placeholder='"Felix found me a listing I would have missed entirely. This is gold."'
          className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm font-serif text-[15px] leading-relaxed"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Author">
          <input
            value={form.author_name}
            onChange={(e) => setField("author_name", e.target.value)}
            placeholder="Sarah Lee"
            className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm"
          />
        </Field>
        <Field label="Account / Brokerage">
          <input
            value={form.account}
            onChange={(e) => setField("account", e.target.value)}
            placeholder="Keller Williams MAC"
            className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm"
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Platform">
          <select
            value={form.platform}
            onChange={(e) => setField("platform", e.target.value as Platform | "")}
            className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm bg-white"
          >
            <option value="">—</option>
            {Object.entries(PLATFORM_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </Field>
        <Field label="Source URL">
          <input
            value={form.source_url}
            onChange={(e) => setField("source_url", e.target.value)}
            placeholder="https://…"
            className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm"
          />
        </Field>
      </div>

      <Field label="Title (optional)">
        <input
          value={form.title}
          onChange={(e) => setField("title", e.target.value)}
          placeholder="One-line headline for this win"
          className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm"
        />
      </Field>

      <Field label="Summary (optional)">
        <input
          value={form.summary}
          onChange={(e) => setField("summary", e.target.value)}
          placeholder="One-sentence context shown beneath the quote"
          className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm"
        />
      </Field>

      <Field label="Tags">
        <input
          value={form.tags}
          onChange={(e) => setField("tags", e.target.value)}
          placeholder="comma, separated"
          className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none text-sm"
        />
      </Field>

      <Field label="Media (screenshots, images, video, or links — add as many as you like)">
        {form.media.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {form.media.map((m, i) => (
              <div key={`${m.url}-${i}`} className="relative group">
                {m.type === "video" && m.mux_playback_id ? (
                  // The stored url is an HLS manifest, which a bare <video> can't play in Chrome (renders black).
                  <img src={`https://image.mux.com/${m.mux_playback_id}/thumbnail.jpg?width=200&height=200&fit_mode=smartcrop`}
                    alt="" className="w-24 h-24 object-cover rounded-lg bg-black" />
                ) : m.type === "video" ? (
                  <video src={m.url} className="w-24 h-24 object-cover rounded-lg bg-black" />
                ) : m.type === "loom" || m.type === "youtube" ? (
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-black grid place-items-center text-white text-[10px] text-center px-1">
                    {m.type === "loom" ? "Loom" : "YouTube"} link
                  </div>
                ) : (
                  <img src={m.url} alt="" className="w-24 h-24 object-cover rounded-lg" />
                )}
                {i === 0 && (
                  <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1 py-0.5 text-[9px] font-medium text-white">Cover</span>
                )}
                <button
                  type="button"
                  onClick={() => removeMediaAt(i)}
                  title="Remove"
                  className="absolute -top-1.5 -right-1.5 grid place-items-center w-5 h-5 rounded-full bg-white border border-black/10 shadow text-red-600 opacity-0 group-hover:opacity-100 transition"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] text-sm text-[hsl(var(--muted-foreground))]"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? `Uploading… ${upProgress}%` : form.media.length ? "Add more" : "Upload images or video"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) uploadFiles(files);
            e.target.value = "";
          }}
        />
        {upErr && <div className="text-xs text-red-600 mt-2">{upErr}</div>}
      </Field>

      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.starred}
          onChange={(e) => setField("starred", e.target.checked)}
          className="rounded border-[hsl(var(--border))]"
        />
        <span className="text-sm inline-flex items-center gap-1">
          <Star className={`w-3.5 h-3.5 ${form.starred ? "fill-amber-400 text-amber-500" : ""}`} />
          Star this win
        </span>
      </label>
    </div>
  );
}

function Field({
  label, required, children,
}: {
  label: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-[hsl(var(--muted-foreground))] mb-1.5">
        {label}{required && <span className="text-red-500"> *</span>}
      </div>
      {children}
    </div>
  );
}
